import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import * as uuid from 'uuid';
import clsx from 'clsx';
import { TimeZone } from 'domains/timezone';
import { formatInTimeZone } from 'date-fns-tz';

type Props = {
  timeSlots: Date[];
  value?: Date;
  labelKey?: string;
  disabled?: boolean;
  timezone?: TimeZone;
  onChange: (value: Date) => void;
};

export const TimeSlotPickerV2: React.FC<Props> = ({
  timeSlots,
  value,
  labelKey,
  disabled = false,
  timezone,
  onChange,
}) => {
  const { format } = useDisplayTimezone();
  const { t } = useTranslation();

  const uniqueId = uuid.v4()[0];

  const timeSlotOptions = useMemo(
    () =>
      timeSlots?.map(d => ({
        label: timezone ? formatInTimeZone(d, timezone, 'h:mm a') : format(d, 'h:mm a'),
        value: d.toISOString(),
      })) ?? [],
    [timeSlots, format, timezone],
  );

  return (
    <div>
      <div className="mb-[8px]">
        <label className="font-inter font-medium text-[13px] leading-[20px]">
          {t(labelKey ?? 'consultation.schedule.availableSlots')}
        </label>
      </div>

      <div
        className={clsx(
          'grid w-full gap-x-e10 gap-y-e14 grid-cols-2',
          'sm:grid-cols-3',
          'md:grid-cols-4',
          'lg:grid-cols-6',
        )}
      >
        {timeSlotOptions.map((slot, index) => (
          <button
            key={slot.value}
            type="button"
            onClick={() => onChange(new Date(slot.value))}
            disabled={disabled}
            className={clsx(
              'font-inter font-medium text-[14px] leading-[20px] text-center px-e16 py-e14 rounded-sm',
              'border',
              value?.toISOString() === slot.value
                ? 'bg-[#EBD7FB] border-[#53534C]'
                : ['bg-white border-[#D4D3CB]', 'hover:bg-[#F7F0FE] hover:border-[#53534C]'],
              disabled && ['cursor-wait'],
            )}
            data-testid={`time-slot-${index}-${uniqueId}`}
          >
            {slot.label}
          </button>
        ))}
      </div>
    </div>
  );
};
